/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IDobbiesBuyboxProductDetailsData } from './dobbies-buybox-product-details.data';
import { IDobbiesBuyboxProductDetailsProps } from './dobbies-buybox-product-details.props.autogenerated';
import { DetailsAccordion, IAccordionData } from './components/accordion/details-accordion';

export interface IDobbiesBuyboxProductDetailsViewProps extends IDobbiesBuyboxProductDetailsProps<IDobbiesBuyboxProductDetailsData> {
    DetailsAccordion: React.ReactNode;
}

/**
 *
 * DobbiesBuyboxProductDetails component
 * @extends {React.PureComponent<IDobbiesBuyboxProductDetailsProps<IDobbiesBuyboxProductDetailsData>>}
 */
class DobbiesBuyboxProductDetails extends React.PureComponent<IDobbiesBuyboxProductDetailsProps<IDobbiesBuyboxProductDetailsData>> {
    public render(): JSX.Element | null {
        const {
            config: { productAttributeHeadings },
            data: {
                productAttributes: { result: attributes },
                product: { result: productData }
            }
        } = this.props;

        const keyFeaturesMeta = attributes?.find(attr => attr.Name === this.props.config.keyFeaturesMetaAttr);
        const floweringTime = attributes?.find(attr => attr.Name === this.props.config.floweringTimeAttr);
        const plantFullfilment = attributes?.find(attr => attr.Name === 'PlantFulfillment');
        const isPFItem = plantFullfilment && plantFullfilment.TextValue === 'Plants';

        const accordianData =
            productAttributeHeadings?.map(item => {
                const attribute = attributes?.find(attr => attr.Name?.toLowerCase() === item.attributeName?.toLowerCase());
                if (item.attributeName === 'Product Description') {
                    return {
                        key: item.attributeHeading,
                        title: item.attributeHeading,
                        content: productData?.Description
                    };
                }
                // Specifications has priority over Composition
                if (item.attributeName === 'Composition') {
                    const specificationsAttr = attributes?.find(attr => attr.Name === 'Specifications');
                    const specificationsValue = specificationsAttr?.TextValue?.trim();

                    if (specificationsValue) {
                        return null;
                    }
                }
                // If item is PF then replace Instructions & safety heading with Planting & care instructions (Configurable in CMS)
                if (item.attributeName === 'Instructions & safety' || item.attributeName === 'Safety') {
                    const instructionValue = attributes?.find(a => a.Name === 'Instructions & safety')?.TextValue?.trim();
                    const safetyValue = attributes?.find(a => a.Name === 'Safety')?.TextValue?.trim();
                    const resolvedValue = instructionValue || safetyValue;
                    const resolvedHeading = instructionValue ? item?.attributeHeading : 'Safety';

                    if (!resolvedValue) {
                        return null;
                    }

                    const plantHeading = productAttributeHeadings.find(h => h.attributeName === 'Planting & care instructions')
                        ?.attributeHeading;

                    const finalHeading = isPFItem ? plantHeading ?? resolvedHeading : resolvedHeading;

                    return {
                        key: finalHeading,
                        title: finalHeading,
                        content: resolvedValue
                    };
                }
                if (attribute) {
                    return {
                        key: attribute.Name || '',
                        title: item.attributeHeading || '',
                        content: attribute.TextValue || ''
                    };
                }
                return null;
            }) || [];

        // Filtering out undefined items;
        const clearAccordianData: (IAccordionData | null)[] = (accordianData ?? []).filter(Boolean).map(item => ({
            key: item?.key ?? '',
            title: item?.title ?? '',
            content: item?.content ?? ''
        }));

        const detailsAccordion = (
            <DetailsAccordion
                data={clearAccordianData}
                apiSettings={this.props.context.request.apiSettings}
                keyFeaturesMeta={keyFeaturesMeta}
                gridSettings={this.props.context.request.gridSettings}
                floweringTime={floweringTime?.TextValue}
                isPFItem={isPFItem}
                config={this.props.config}
            />
        );

        return this.props.renderView({
            ...this.props,
            DetailsAccordion: detailsAccordion
        });
    }
}

export default DobbiesBuyboxProductDetails;
